/**
 * SecuritySection — Ayarlar > Güvenlik
 *
 * Şifre değiştirme akışı. Mevcut şifreyi sorar (session hijack koruması),
 * yeni şifre 8+ karakter, tekrarı ile eşleşmeli. Backend zaten aynı
 * doğrulamayı yapıyor; UI de aynı kuralları uygulayıp anında feedback verir.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { KeyRound, Eye, EyeOff, ShieldCheck } from "lucide-react";
import RecoveryCodePanel from "./RecoveryCodePanel";

/** Basic strength heuristic: length + character variety. */
function strength(pw) {
  if (!pw) return { score: 0, label: "" };
  let s = 0;
  if (pw.length >= 8) s += 1;
  if (pw.length >= 12) s += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s += 1;
  if (/\d/.test(pw)) s += 1;
  if (/[^A-Za-z0-9]/.test(pw)) s += 1;
  const bands = [
    { min: 0, label: "Zayıf", tone: "bg-red-500" },
    { min: 2, label: "Orta", tone: "bg-amber-500" },
    { min: 4, label: "Güçlü", tone: "bg-emerald-500" },
  ];
  const band = bands.reduce((acc, b) => (s >= b.min ? b : acc), bands[0]);
  return { score: s, label: band.label, tone: band.tone };
}

export default function SecuritySection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const user = useAuthStore((s) => s.user);
  const isUltra = user?.role === "ultra_admin";

  const meter = useMemo(() => strength(next), [next]);
  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 8;
  const canSubmit =
    current.length > 0 && next.length >= 8 && next === confirm && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      await api.post("/users/me/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Şifren güncellendi");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Değiştirilemedi",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <form
        onSubmit={submit}
        className="max-w-2xl space-y-5 rounded-lg border border-zinc-200 bg-white p-6"
        data-testid="security-section"
      >
      <div className="flex items-center gap-2 border-b border-zinc-100 pb-3">
        <KeyRound className="h-4 w-4 text-zinc-500" strokeWidth={2} />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-700">
          Güvenlik
        </h2>
      </div>
      <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} />
        <div>
          <div className="font-semibold">Güvenlik önerisi</div>
          <div className="mt-0.5">
            Şifren en az 8 karakter olsun. Büyük–küçük harf, sayı ve sembolü
            karıştır. Aynı şifreyi başka yerlerde kullanma.
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1">
          <KeyRound className="h-3 w-3" /> Mevcut şifre
        </Label>
        <Input
          data-testid="security-current-input"
          type="password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
          className="mt-1"
          placeholder="••••••••"
        />
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center justify-between">
          <span className="flex items-center gap-1">
            <KeyRound className="h-3 w-3" /> Yeni şifre
          </span>
          <button
            type="button"
            onClick={() => setShowNext((v) => !v)}
            className="inline-flex items-center gap-1 text-[10px] font-normal text-zinc-500 hover:text-zinc-800"
            data-testid="security-toggle-visibility"
          >
            {showNext ? (
              <>
                <EyeOff className="h-3 w-3" /> Gizle
              </>
            ) : (
              <>
                <Eye className="h-3 w-3" /> Göster
              </>
            )}
          </button>
        </Label>
        <Input
          data-testid="security-new-input"
          type={showNext ? "text" : "password"}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
          className="mt-1"
          placeholder="En az 8 karakter"
        />
        {next && (
          <div className="mt-1.5 flex items-center gap-2">
            <div className="flex-1 h-1 rounded-full bg-zinc-100 overflow-hidden">
              <div
                className={`h-full transition-all ${meter.tone}`}
                style={{ width: `${Math.min(meter.score, 5) * 20}%` }}
              />
            </div>
            <span className="text-[10px] font-medium text-zinc-500">
              {meter.label}
            </span>
          </div>
        )}
        {tooShort && (
          <p className="mt-1 text-[11px] text-red-600">
            Şifre en az 8 karakter olmalı
          </p>
        )}
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1">
          <KeyRound className="h-3 w-3" /> Yeni şifre (tekrar)
        </Label>
        <Input
          data-testid="security-confirm-input"
          type={showNext ? "text" : "password"}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          autoComplete="new-password"
          className="mt-1"
          placeholder="Yeni şifreyi tekrar yaz"
        />
        {mismatch && (
          <p className="mt-1 text-[11px] text-red-600">Şifreler eşleşmiyor</p>
        )}
      </div>

      <div className="flex justify-end border-t border-zinc-100 pt-4">
        <Button
          type="submit"
          disabled={!canSubmit}
          data-testid="security-submit"
          className="bg-zinc-900 text-white hover:bg-zinc-800"
        >
          {saving ? "Değiştiriliyor..." : "Şifreyi değiştir"}
        </Button>
      </div>
    </form>
    {isUltra && <RecoveryCodePanel />}
  </div>
  );
}
